import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Cpu, Globe, Terminal, Shield, Menu, X } from 'lucide-react';
import './Navbar.css';

const servicesList = [
  { name: "Digital Products", path: "/services#digital-products", icon: <Globe size={20} />, desc: "Web & mobile applications" },
  { name: "Software & Systems", path: "/services#software-business-systems", icon: <Terminal size={20} />, desc: "Custom backends & integrations" },
  { name: "Intelligent Workflows", path: "/services#intelligent-transformation", icon: <Cpu size={20} />, desc: "AI & workflow automation" },
  { name: "Infrastructure & Security", path: "/services#infrastructure-security", icon: <Shield size={20} />, desc: "Cloud DevOps & security" }
];

const Navbar = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  React.useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileServicesOpen(false);
  }, [location]);

  React.useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const handleLinkClick = (e, id) => {
    setIsMobileMenuOpen(false);
    if (isHome && id) {
      e.preventDefault();
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className="header-fixed">
      <nav className={`navbar-pill ${isScrolled ? 'scrolled' : ''}`}>
        <div className="navbar-container">
          <Link to="/" className="navbar-logo">
            <div className="logo-background">
                <img src="/Logo/logo usable.png" alt="Kodebuild Logo" className="logo-img" />
            </div>
          </Link>

          <ul className="navbar-links">
            <li><Link to="/" onClick={(e) => handleLinkClick(e, 'hero')} className="nav-link modern-underline">Home</Link></li>
            <li><Link to="/solutions" className="nav-link modern-underline">Solutions</Link></li>
            <li
              className="mega-menu-trigger"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <Link to="/services" className="nav-link modern-underline cursor-pointer">
                Services <ChevronDown size={14} className={`chevron ${isDropdownOpen ? 'rotate' : ''}`} />
              </Link>
              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    className="mega-menu"
                    initial={{ opacity: 0, y: 6, scaleY: 0.96, translateX: '-50%' }}
                    animate={{ opacity: 1, y: 0, scaleY: 1, translateX: '-50%' }}
                    exit={{ opacity: 0, y: 6, scaleY: 0.96, translateX: '-50%' }}
                    transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                    style={{ originY: 0 }}
                  >
                    <div className="mega-menu-list">
                      {servicesList.map((service, index) => (
                        <Link key={index} to={service.path} className="mega-menu-item">
                          <div className="item-icon">{service.icon}</div>
                          <div className="item-info">
                            <span className="item-name">{service.name}</span>
                            <span className="item-desc">{service.desc}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
            <li><Link to="/work" className="nav-link modern-underline">Our Work</Link></li>
            <li><Link to="/about" className="nav-link modern-underline">About</Link></li>
            <li><Link to="/contact" className="nav-link modern-underline">Contact</Link></li>
          </ul>

          <div className="navbar-cta">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link to="/contact" className="btn-primary glow-hover">Get Started</Link>
            </motion.div>
          </div>

          <button
            className={`hamburger-btn ${isMobileMenuOpen ? 'is-open' : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <AnimatePresence mode="wait" initial={false}>
              {isMobileMenuOpen ? (
                <motion.span key="close"
                  initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }}
                >
                  <X size={24} />
                </motion.span>
              ) : (
                <motion.span key="open"
                  initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.18 }}
                >
                  <Menu size={24} />
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              className="mobile-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              className="mobile-menu-drawer glass"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            >
              <nav className="mobile-menu-nav">
                <Link to="/" onClick={(e) => handleLinkClick(e, 'hero')} className="mobile-nav-link">Home</Link>
                <Link to="/solutions" onClick={() => setIsMobileMenuOpen(false)} className="mobile-nav-link">Solutions</Link>
                
                <div className="mobile-services-group">
                  <button
                    className="mobile-nav-link mobile-services-toggle"
                    onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  >
                    <span>Services</span>
                    <ChevronDown size={16} className={`chevron ${isMobileServicesOpen ? 'rotate' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {isMobileServicesOpen && (
                      <motion.div
                        className="mobile-services-list"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: 'hidden' }}
                      >
                        {servicesList.map((service, index) => (
                          <Link
                            key={index}
                            to={service.path}
                            className="mobile-service-item"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <span className="mobile-service-icon">{service.icon}</span>
                            <span>{service.name}</span>
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <Link to="/work" onClick={() => setIsMobileMenuOpen(false)} className="mobile-nav-link">Our Work</Link>
                <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="mobile-nav-link">About</Link>
                <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="mobile-nav-link">Contact</Link>

                <div className="mobile-menu-cta">
                  <Link to="/contact" className="btn-primary" onClick={() => setIsMobileMenuOpen(false)}>
                    Get Started
                  </Link>
                </div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
